import React, { useEffect, useState } from 'react';
import { Building2, Users, FileText, Activity, Wrench, DollarSign, TrendingUp, CalendarPlus } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import toast from 'react-hot-toast';

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value ?? '-'}</p>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/platform/dashboard')
      .then(res => setData(res.data))
      .catch(err => toast.error(err.response?.data?.error || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-gray-200 rounded" />
          <div className="grid grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-gray-200 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
        <StatCard icon={Building2} label="Total Tenants" value={data.stats.total_tenants} color="bg-blue-600" />
        <StatCard icon={Activity} label="Active Tenants" value={data.stats.active_tenants} color="bg-green-600" />
        <StatCard icon={Users} label="Total Users" value={data.stats.total_users} color="bg-purple-600" />
        <StatCard icon={FileText} label="Total Records" value={data.stats.total_records} color="bg-orange-500" />
        <StatCard icon={TrendingUp} label="New This Week" value={data.stats.new_this_week} color="bg-teal-600" />
        <StatCard icon={CalendarPlus} label="New This Month" value={data.stats.new_this_month} color="bg-indigo-600" />
      </div>

      {/* Services Overview */}
      {data.services_overview && data.services_overview.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Services Overview</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {data.services_overview.map(svc => {
              const providerCost = parseFloat(svc.month_provider_cost || 0);
              const billedCost = parseFloat(svc.month_billed_cost || 0);
              const margin = billedCost - providerCost;
              return (
                <div key={svc.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Wrench className="w-4 h-4 text-gray-400" />
                    <p className="text-sm font-medium text-gray-900 truncate">{svc.name}</p>
                  </div>
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <p className="text-xs text-gray-500">Tenants</p>
                      <p className="text-sm font-semibold text-gray-900">{svc.enabled_tenants || 0}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Usage</p>
                      <p className="text-sm font-semibold text-gray-900">{parseFloat(svc.month_usage || 0).toLocaleString()}</p>
                    </div>
                  </div>
                  {billedCost > 0 && (
                    <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                      <DollarSign className="w-3 h-3" />
                      <span>${billedCost.toFixed(2)} billed</span>
                      {margin > 0 && <span className="text-green-600">(+${margin.toFixed(2)})</span>}
                    </div>
                  )}
                  <span className={`inline-block mt-1 px-1.5 py-0.5 text-xs rounded-full ${svc.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {svc.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Signups */}
      {data.recent_signups && data.recent_signups.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Signups</h2>
            <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
              {data.recent_signups.length}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                  <th className="pb-2 font-medium">Firm Name</th>
                  <th className="pb-2 font-medium">Owner</th>
                  <th className="pb-2 font-medium">Email</th>
                  <th className="pb-2 font-medium">Plan</th>
                  <th className="pb-2 font-medium text-right">Signed Up</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_signups.map(s => {
                  const hoursAgo = (Date.now() - new Date(s.created_at).getTime()) / (1000 * 60 * 60);
                  const isNew = hoursAgo < 24;
                  return (
                    <tr
                      key={s.id}
                      className="border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/tenants/${s.id}`)}
                    >
                      <td className="py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{s.name}</span>
                          {isNew && (
                            <span className="bg-green-100 text-green-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase">New</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 text-gray-700">{s.owner_name || '—'}</td>
                      <td className="py-2.5 text-gray-500">{s.owner_email || '—'}</td>
                      <td className="py-2.5">
                        <span className="capitalize text-gray-700">{s.plan_name}</span>
                      </td>
                      <td className="py-2.5 text-right text-gray-400">
                        {formatDistanceToNow(new Date(s.created_at), { addSuffix: true })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Tenants */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Tenants</h2>
          {data.recent_tenants.length === 0 ? (
            <p className="text-sm text-gray-500">No recent tenants</p>
          ) : (
            <div className="space-y-3">
              {data.recent_tenants.map(t => (
                <div key={t.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-medium text-gray-900">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.slug}</p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
                      t.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {t.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">{format(new Date(t.created_at), 'MMM d, yyyy')}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Users by Tenant */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Users by Tenant</h2>
          {data.users_by_tenant.length === 0 ? (
            <p className="text-sm text-gray-500">No data</p>
          ) : (
            <div className="space-y-3">
              {data.users_by_tenant.map(t => (
                <div key={t.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-medium text-gray-900">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.slug}</p>
                  </div>
                  <span className="text-sm font-semibold text-gray-700">
                    {t.user_count} user{parseInt(t.user_count) !== 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
